/**
 * GrantCoinsDialog.jsx — admin's "infinite supply" coin gift modal.
 *
 * Triggered from a small button in the TopBar (admin-only, real role).
 * Lets the admin gift a fixed amount or a random range to any user by
 * email. POSTs to /api/admin/grant-coins or /api/admin/grant-coins/random.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Coins, Gift, X, Loader2, Sparkles, Check, Search, ShieldCheck, BookOpen, PenLine } from 'lucide-react';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ROLE_META = {
  admin:  { label: 'Admin',  Icon: ShieldCheck, color: 'text-purple-300' },
  writer: { label: 'Scribe', Icon: PenLine,     color: 'text-amber-300' },
  user:   { label: 'Reader', Icon: BookOpen,    color: 'text-indigo-300' },
};

const GrantCoinsDialog = ({ open, onClose, defaultEmail = '' }) => {
  const [mode, setMode] = useState('fixed'); // fixed | random
  const [email, setEmail] = useState(defaultEmail);
  const [amount, setAmount] = useState(1000);
  const [randMin, setRandMin] = useState(50);
  const [randMax, setRandMax] = useState(500);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // User-picker state — fetched once on open, then locally filtered.
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [search, setSearch] = useState('');
  // Per-row "row-busy" state — shows the spinner only on the row being
  // gifted, not the whole dialog. Declared up here (with the other hooks)
  // because Rules of Hooks forbids it after the `if (!open) return null;`.
  const [rowBusy, setRowBusy] = useState(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!open || fetchedRef.current) return;
    fetchedRef.current = true;
    setUsersLoading(true);
    (async () => {
      try {
        const res = await authFetch(`${BACKEND_URL}/api/admin/users`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const list = Array.isArray(data) ? data : (data.users || data.data || []);
        // Sort: readers first by name, then writers, then admins. Most-used
        // gift target ends up on top.
        const order = { user: 0, writer: 1, admin: 2 };
        list.sort((a, b) => {
          const ra = order[a.role] ?? 9;
          const rb = order[b.role] ?? 9;
          if (ra !== rb) return ra - rb;
          return (a.name || a.email || '').localeCompare(b.name || b.email || '');
        });
        setUsers(list);
      } catch (e) {
        setUsersError(typeof e?.message === 'string' ? e.message : 'Failed to load users');
      } finally {
        setUsersLoading(false);
      }
    })();
  }, [open]);

  // Filter the user list by name, email, role, or user_id — case-insensitive.
  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      (u.email || '').toLowerCase().includes(q) ||
      (u.name || '').toLowerCase().includes(q) ||
      (u.role || '').toLowerCase().includes(q) ||
      (u.user_id || '').toLowerCase().includes(q)
    );
  }, [users, search]);

  if (!open) return null;

  const submit = async (overrideEmail = null) => {
    const target = (overrideEmail || email).trim();
    if (!target) return;
    if (overrideEmail) setRowBusy(target); else setBusy(true);
    setError('');
    setResult(null);
    try {
      const url = mode === 'random'
        ? `${BACKEND_URL}/api/admin/grant-coins/random`
        : `${BACKEND_URL}/api/admin/grant-coins`;
      const body = mode === 'random'
        ? { target_email: target, min: Number(randMin), max: Number(randMax), note: note || null }
        : { target_email: target, amount: Number(amount), note: note || null };
      const res = await authFetch(url, { method: 'POST', body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) {
        const detail = typeof data?.detail === 'string'
          ? data.detail
          : Array.isArray(data?.detail)
            ? data.detail.map((d) => d.msg).join(', ')
            : 'Grant failed';
        throw new Error(detail);
      }
      setResult({ ...data, target_email: target });
    } catch (e) {
      setError(typeof e?.message === 'string' ? e.message : 'Grant failed');
    } finally {
      setBusy(false);
      setRowBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="grant-coins-dialog">
      <div className="relative max-w-md w-full bg-slate-900 border border-purple-500/40 rounded-2xl shadow-2xl shadow-purple-900/40 p-6">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-slate-400 hover:text-white"
          aria-label="Close"
          data-testid="grant-close"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2 mb-4">
          <Gift className="w-6 h-6 text-purple-300" />
          <h2 className="text-xl font-bold text-white">Gift coins</h2>
          <span className="text-[10px] uppercase tracking-widest font-bold bg-purple-600/30 text-purple-200 px-2 py-0.5 rounded-full">
            ∞ Admin
          </span>
        </div>
        <p className="text-xs text-slate-400 mb-4">
          Coins are minted from the platform supply. Every grant is logged for audit.
        </p>

        {/* Mode toggle */}
        <div className="inline-flex p-0.5 rounded-full bg-slate-800 border border-slate-700 mb-4">
          {['fixed', 'random'].map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`px-3 py-1 rounded-full text-xs font-bold capitalize ${
                mode === m ? 'bg-purple-600 text-white' : 'text-slate-400 hover:text-white'
              }`}
              data-testid={`grant-mode-${m}`}
            >
              {m === 'random' && <Sparkles className="w-3 h-3 inline mr-1" />}
              {m}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-slate-400 font-bold">Recipient email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="reader@example.com"
              className="mt-1 w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-purple-400 focus:outline-none"
              data-testid="grant-email"
            />
          </label>

          {/* Browse + search all users — admin can pick from the full list
              instead of typing the email by hand. Case-insensitive search
              matches email, username, role, and user_id. */}
          <div className="rounded-lg border border-slate-700/80 bg-slate-800/40 overflow-hidden" data-testid="grant-user-picker">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-700/60">
              <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search users by name, email, role…"
                className="flex-1 bg-transparent text-white text-sm placeholder-slate-500 focus:outline-none"
                data-testid="grant-user-search"
              />
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold tabular-nums">
                {filteredUsers.length}/{users.length}
              </span>
            </div>
            <div className="max-h-48 overflow-y-auto" data-testid="grant-user-list">
              {usersLoading && (
                <div className="flex items-center justify-center py-6 text-slate-400 text-xs">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading users…
                </div>
              )}
              {usersError && !usersLoading && (
                <div className="px-3 py-3 text-xs text-rose-300" data-testid="grant-user-error">
                  Couldn't load users — {usersError}
                </div>
              )}
              {!usersLoading && !usersError && filteredUsers.length === 0 && (
                <div className="px-3 py-4 text-center text-xs text-slate-500" data-testid="grant-user-empty">
                  No users match "{search}".
                </div>
              )}
              {!usersLoading && filteredUsers.map((u) => {
                const meta = ROLE_META[u.role] || ROLE_META.user;
                const RoleIcon = meta.Icon;
                const isSelected = email === u.email;
                const rowGifting = rowBusy === u.email;
                return (
                  <div
                    key={u.user_id || u.email}
                    className={`w-full flex items-center gap-2 px-3 py-2 border-b border-slate-700/30 last:border-b-0 transition-colors ${
                      isSelected ? 'bg-purple-600/20' : 'hover:bg-slate-800/80'
                    }`}
                    data-testid={`grant-user-row-${u.user_id || u.email}`}
                  >
                    <button
                      type="button"
                      onClick={() => setEmail(u.email)}
                      className="flex-1 flex items-center gap-2.5 min-w-0 text-left"
                      data-testid={`grant-user-select-${u.user_id || u.email}`}
                    >
                      <RoleIcon className={`w-4 h-4 flex-shrink-0 ${meta.color}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-white truncate">{u.name || '—'}</div>
                        <div className="text-[11px] text-slate-400 truncate">{u.email}</div>
                      </div>
                      <span className={`text-[9px] uppercase tracking-widest font-bold ${meta.color} hidden xs:inline`}>
                        {meta.label}
                      </span>
                    </button>
                    {/* Inline Gift column — fires the grant against this row's
                        email using the current Fixed/Random + Amount settings.
                        Spinner is row-scoped so multiple admins can fire in
                        rapid succession without locking the whole dialog. */}
                    <button
                      type="button"
                      onClick={() => submit(u.email)}
                      disabled={rowGifting || busy}
                      className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-purple-600 hover:bg-purple-500 disabled:opacity-60 text-white text-[11px] font-bold shadow-md shadow-purple-900/40"
                      data-testid={`grant-user-gift-${u.user_id || u.email}`}
                      title={mode === 'random' ? `Surprise gift ${randMin}–${randMax}` : `Gift ${Number(amount).toLocaleString()} coins`}
                    >
                      {rowGifting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Gift className="w-3 h-3" />}
                      Gift
                    </button>
                    {isSelected && <Check className="w-3.5 h-3.5 text-purple-300 flex-shrink-0" />}
                  </div>
                );
              })}
            </div>
          </div>

          {mode === 'fixed' ? (
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-slate-400 font-bold">Amount</span>
              <div className="mt-1 flex items-center gap-2">
                <Coins className="w-4 h-4 text-amber-300" />
                <input
                  type="number"
                  min={1}
                  max={1000000}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm tabular-nums focus:border-purple-400 focus:outline-none"
                  data-testid="grant-amount"
                />
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {[5, 100, 500, 1000, 5000, 10000].map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => setAmount(q)}
                    className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-800 border border-slate-700 text-slate-300 hover:border-purple-400"
                    data-testid={`grant-quick-${q}`}
                  >
                    {q.toLocaleString()}
                  </button>
                ))}
              </div>
            </label>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="text-xs uppercase tracking-widest text-slate-400 font-bold">Min</span>
                  <input
                    type="number"
                    min={1}
                    value={randMin}
                    onChange={(e) => setRandMin(e.target.value)}
                    className="mt-1 w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm tabular-nums"
                    data-testid="grant-min"
                  />
                </label>
                <label className="block">
                  <span className="text-xs uppercase tracking-widest text-slate-400 font-bold">Max</span>
                  <input
                    type="number"
                    min={1}
                    value={randMax}
                    onChange={(e) => setRandMax(e.target.value)}
                    className="mt-1 w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm tabular-nums"
                    data-testid="grant-max"
                  />
                </label>
              </div>
              {/* Quick-range presets — micro-rewards on the left, larger
                  community-sized drops on the right. */}
              <div className="flex flex-wrap gap-1.5">
                {[
                  { label: '🎲 5–25', min: 5, max: 25 },
                  { label: '50–250', min: 50, max: 250 },
                  { label: '100–1k', min: 100, max: 1000 },
                  { label: '500–5k', min: 500, max: 5000 },
                ].map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => { setRandMin(p.min); setRandMax(p.max); }}
                    className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-800 border border-slate-700 text-slate-300 hover:border-purple-400"
                    data-testid={`grant-random-quick-${p.min}-${p.max}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <label className="block">
            <span className="text-xs uppercase tracking-widest text-slate-400 font-bold">Note (optional)</span>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={240}
              placeholder="Surprise gift, contest reward, etc."
              className="mt-1 w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:border-purple-400 focus:outline-none"
              data-testid="grant-note"
            />
          </label>
        </div>

        {error && (
          <p className="mt-3 text-sm text-rose-300" data-testid="grant-error">{error}</p>
        )}
        {result && (
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/15 border border-emerald-400/40" data-testid="grant-result">
            <Check className="w-4 h-4 text-emerald-300" />
            <span className="text-sm text-emerald-200">
              Granted <span className="font-bold tabular-nums">{result.amount}</span> coins
              {result.target_email ? <> to <span className="font-bold">{result.target_email}</span></> : null}. New balance:{' '}
              <span className="font-bold tabular-nums">{result.new_balance}</span>
            </span>
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-full text-sm font-semibold text-slate-300 hover:text-white"
          >
            Close
          </button>
          <button
            type="button"
            onClick={() => submit()}
            disabled={busy || !email.trim()}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-purple-600 hover:bg-purple-500 disabled:opacity-60 text-white font-bold text-sm shadow-lg shadow-purple-900/40"
            data-testid="grant-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
            {busy ? 'Granting…' : mode === 'random' ? 'Surprise gift' : 'Gift coins'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GrantCoinsDialog;
